import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { translations, languageNames, languageFlags } from '../lib/translations';
import { 
  ArrowLeft, 
  Plus, 
  PencilSimple, 
  Trash, 
  FileText, 
  Copy,
  DotsSixVertical,
  ArrowUp,
  ArrowDown,
  TextT,
  ListBullets,
  Sparkle
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import ImportContractDialog from '../components/ImportContractDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const languages = ['ITA', 'ENG', 'FRA', 'SPA', 'TED'];

const defaultSections = [
  {
    id: 'intro',
    title: 'PREMESSA',
    content: 'Con il presente contratto, le parti convengono quanto segue per la locazione dell\'immobile ad uso turistico.'
  },
  {
    id: 'rules',
    title: 'REGOLE DELLA CASA',
    content: '1. È vietato fumare all\'interno dei locali.\n2. Rispettare gli orari di silenzio (23:00 - 08:00).\n3. Non è consentito ospitare persone non registrate.\n4. L\'immobile deve essere riconsegnato pulito e in ordine.'
  },
  {
    id: 'damages',
    title: 'DANNI E RESPONSABILITÀ',
    content: 'Il Conduttore è responsabile di eventuali danni causati all\'immobile o agli arredi durante il soggiorno.'
  },
  {
    id: 'privacy',
    title: 'PRIVACY',
    content: 'I dati saranno trattati esclusivamente per obblighi di legge ai sensi del GDPR 679/2016.'
  }
];

export default function TemplatesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const lang = user?.preferred_language || 'ITA';
  const t = translations[lang];

  const [formData, setFormData] = useState({
    name: '',
    language: 'ITA',
    title: 'CONTRATTO DI LOCAZIONE AD USO TURISTICO',
    subtitle: '',
    sections: [...defaultSections]
  });

  const [editingSection, setEditingSection] = useState(null);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [sectionForm, setSectionForm] = useState({ title: '', content: '' });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`, { withCredentials: true });
      setTemplates(response.data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Inserisci un nome per il template');
      return;
    }

    const content = JSON.stringify({
      title: formData.title,
      subtitle: formData.subtitle,
      sections: formData.sections
    });

    try {
      if (editingTemplate) {
        await axios.put(`${API}/templates/${editingTemplate.id}`, {
          name: formData.name,
          language: formData.language,
          content
        }, { withCredentials: true });
        toast.success('Template aggiornato');
      } else {
        await axios.post(`${API}/templates`, {
          name: formData.name,
          language: formData.language,
          content
        }, { withCredentials: true });
        toast.success('Template creato');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvare');
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      language: 'ITA',
      title: 'CONTRATTO DI LOCAZIONE AD USO TURISTICO',
      subtitle: '(Ai sensi dell\'art. 1, comma 2, lett. c, Legge 09/12/1998 n. 431)',
      sections: [...defaultSections]
    });
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    try {
      const parsed = JSON.parse(template.content);
      setFormData({
        name: template.name,
        language: template.language,
        title: parsed.title || '',
        subtitle: parsed.subtitle || '',
        sections: parsed.sections || []
      });
    } catch {
      setFormData({
        name: template.name,
        language: template.language,
        title: '',
        subtitle: '',
        sections: []
      });
    }
    setIsDialogOpen(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Eliminare questo template?')) return;
    try {
      await axios.delete(`${API}/templates/${templateId}`, { withCredentials: true });
      toast.success('Template eliminato');
      fetchTemplates();
    } catch (err) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleNew = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const duplicateTemplate = async (template) => {
    try {
      await axios.post(`${API}/templates`, {
        name: `${template.name} (copia)`,
        language: template.language,
        content: template.content
      }, { withCredentials: true });
      toast.success('Template duplicato');
      fetchTemplates();
    } catch (err) {
      toast.error('Errore nella duplicazione');
    }
  };

  // Section management
  const addSection = () => {
    setEditingSection(null);
    setSectionForm({ title: '', content: '' });
    setSectionDialogOpen(true);
  };

  const editSection = (index) => {
    setEditingSection(index);
    setSectionForm({
      title: formData.sections[index].title,
      content: formData.sections[index].content
    });
    setSectionDialogOpen(true);
  };

  const saveSection = () => {
    if (!sectionForm.title) {
      toast.error('Inserisci un titolo');
      return;
    }

    const newSections = [...formData.sections];
    if (editingSection !== null) {
      newSections[editingSection] = {
        id: formData.sections[editingSection].id,
        title: sectionForm.title,
        content: sectionForm.content
      };
    } else {
      newSections.push({
        id: `section_${Date.now()}`,
        title: sectionForm.title,
        content: sectionForm.content
      });
    }
    setFormData(prev => ({ ...prev, sections: newSections }));
    setSectionDialogOpen(false);
  };

  const deleteSection = (index) => {
    const newSections = formData.sections.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, sections: newSections }));
  };

  const moveSection = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= formData.sections.length) return;
    
    const newSections = [...formData.sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setFormData(prev => ({ ...prev, sections: newSections }));
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-zinc-100 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={24} weight="bold" className="text-zinc-700" />
          </button>
          <div>
            <h1 className="text-lg sm:text-xl font-black text-zinc-900 tracking-tight">Template Personalizzati</h1>
            <p className="text-xs sm:text-sm text-zinc-500">Crea modelli di contratto su misura</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
          <p className="text-zinc-500">{templates.length} template salvati</p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <Button
              onClick={() => setImportDialogOpen(true)}
              className="bg-teal-600 hover:bg-teal-700 text-white font-bold uppercase tracking-wider"
              data-testid="import-template-button"
            >
              <Sparkle size={18} weight="fill" className="mr-2" />
              Importa da PDF/Word
            </Button>
            <Button
              onClick={handleNew}
              className="bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider"
              data-testid="new-template-button"
            >
              <Plus size={20} className="mr-2" />
              Nuovo Template
            </Button>
          </div>
        </div>

        <ImportContractDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          onSaved={() => fetchTemplates()}
        />

        {/* Template Editor Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-black">
                {editingTemplate ? 'Modifica Template' : 'Nuovo Template'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">
                    Nome Template *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="es. Contratto Standard"
                    className="rounded-none mt-2"
                    data-testid="template-name-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">
                    Lingua
                  </Label>
                  <Select 
                    value={formData.language} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, language: v }))}
                  >
                    <SelectTrigger className="rounded-none mt-2" data-testid="template-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map(l => (
                        <SelectItem key={l} value={l}>
                          <span className="flex items-center gap-2">
                            <span>{languageFlags[l]}</span>
                            <span>{languageNames[l]}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Title & Subtitle */}
              <div className="border border-zinc-200 p-4 bg-zinc-50">
                <div className="flex items-center gap-2 mb-4">
                  <TextT size={20} className="text-zinc-600" />
                  <span className="font-bold text-zinc-700">Intestazione</span>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-zinc-600">Titolo Contratto</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="rounded-none mt-1"
                      data-testid="template-title-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-zinc-600">Sottotitolo (riferimento normativo)</Label>
                    <Input
                      value={formData.subtitle}
                      onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                      className="rounded-none mt-1"
                      data-testid="template-subtitle-input"
                    />
                  </div>
                </div>
              </div>

              {/* Sections */}
              <div className="border border-zinc-200 p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ListBullets size={20} className="text-zinc-600" />
                    <span className="font-bold text-zinc-700">Sezioni del Contratto</span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addSection}
                    className="rounded-none"
                    data-testid="add-section-button"
                  >
                    <Plus size={16} className="mr-1" />
                    Aggiungi Sezione
                  </Button>
                </div>

                <p className="text-xs text-zinc-500 mb-4">
                  Trascina le sezioni per riordinarle. I dati di proprietario, ospite, immobile e pagamento vengono aggiunti automaticamente.
                </p>

                {formData.sections.length === 0 ? (
                  <div className="text-center py-8 text-zinc-400">
                    Nessuna sezione personalizzata. Clicca "Aggiungi Sezione" per iniziare.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.sections.map((section, index) => (
                      <div 
                        key={section.id}
                        className="flex items-start gap-2 p-3 bg-white border border-zinc-200 hover:border-zinc-400 transition-colors"
                      >
                        <div className="flex flex-col gap-1 pt-1">
                          <button
                            type="button"
                            onClick={() => moveSection(index, -1)}
                            disabled={index === 0}
                            className="p-1 hover:bg-zinc-100 disabled:opacity-30"
                            data-testid={`move-up-${index}`}
                          >
                            <ArrowUp size={14} className="text-zinc-500" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveSection(index, 1)}
                            disabled={index === formData.sections.length - 1}
                            className="p-1 hover:bg-zinc-100 disabled:opacity-30"
                            data-testid={`move-down-${index}`}
                          >
                            <ArrowDown size={14} className="text-zinc-500" />
                          </button>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-zinc-900 text-sm">{section.title}</h4>
                          <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{section.content}</p>
                        </div>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => editSection(index)}
                            className="p-2 hover:bg-zinc-100 transition-colors"
                            data-testid={`edit-section-${index}`}
                          >
                            <PencilSimple size={16} className="text-zinc-600" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteSection(index)}
                            className="p-2 hover:bg-red-50 transition-colors"
                            data-testid={`delete-section-${index}`}
                          >
                            <Trash size={16} className="text-red-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Save Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="flex-1 rounded-none border-zinc-300"
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleSave}
                  className="flex-1 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider"
                  data-testid="save-template-button"
                >
                  {editingTemplate ? 'Aggiorna' : 'Salva Template'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Section Editor Dialog */}
        <Dialog open={sectionDialogOpen} onOpenChange={setSectionDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">
                {editingSection !== null ? 'Modifica Sezione' : 'Nuova Sezione'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">
                  Titolo Sezione *
                </Label>
                <Input
                  value={sectionForm.title}
                  onChange={(e) => setSectionForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="es. REGOLE DELLA CASA"
                  className="rounded-none mt-2"
                  data-testid="section-title-input"
                />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-zinc-700">
                  Contenuto
                </Label>
                <Textarea
                  value={sectionForm.content}
                  onChange={(e) => setSectionForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Scrivi il contenuto della sezione..."
                  className="rounded-none mt-2 min-h-[150px]"
                  data-testid="section-content-textarea"
                />
              </div>
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSectionDialogOpen(false)}
                  className="flex-1 rounded-none border-zinc-300"
                >
                  Annulla
                </Button>
                <Button
                  onClick={saveSection}
                  className="flex-1 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold"
                  data-testid="save-section-button"
                >
                  Salva
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Templates List */}
        {loading ? (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">
            Caricamento...
          </div>
        ) : templates.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center">
            <FileText size={48} className="text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500 mb-4">Nessun template personalizzato</p>
            <p className="text-sm text-zinc-400 mb-6">
              Crea template su misura con le tue clausole e regole personalizzate
            </p>
            <Button
              onClick={handleNew}
              className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider"
            >
              Crea il primo template
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {templates.map((template) => {
              let parsed = {};
              try {
                parsed = JSON.parse(template.content);
              } catch {}
              
              return (
                <div 
                  key={template.id} 
                  className="bg-white border border-zinc-200 p-6 flex items-start justify-between"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xl">{languageFlags[template.language]}</span>
                      <h3 className="font-bold text-zinc-900">{template.name}</h3>
                    </div>
                    <p className="text-sm text-zinc-500 mb-2">
                      {parsed.title || 'Template personalizzato'}
                    </p>
                    <p className="text-xs text-zinc-400">
                      {parsed.sections?.length || 0} sezioni personalizzate
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => duplicateTemplate(template)}
                      className="p-2 hover:bg-zinc-100 transition-colors"
                      title="Duplica"
                      data-testid={`duplicate-template-${template.id}`}
                    >
                      <Copy size={18} className="text-zinc-600" />
                    </button>
                    <button
                      onClick={() => handleEdit(template)}
                      className="p-2 hover:bg-zinc-100 transition-colors"
                      title="Modifica"
                      data-testid={`edit-template-${template.id}`}
                    >
                      <PencilSimple size={18} className="text-zinc-600" />
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="p-2 hover:bg-red-50 transition-colors"
                      title="Elimina"
                      data-testid={`delete-template-${template.id}`}
                    >
                      <Trash size={18} className="text-red-500" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
